import { Box, BoxProps, Theme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import ArrowRightIcon from '@watershed/icons/components/ArrowRight';
import PopOutIcon from '@watershed/icons/components/PopOut';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React from 'react';
import { navigateToPathOnClick } from '../utils/NavigationUtils';
import BoxLink from './BoxLink';

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    list: {
      listStyle: 'none',
      border: `1px solid ${theme.palette.grey20}`,
      borderRadius: 6,
      overflow: 'clip',
      margin: 0,
      boxShadow: '0px 1px 0.5px 0px rgba(79, 89, 110, 0.08)',
    },
    listItem: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.white,
      transition: theme.transitions.create('background-color'),
      borderTop: `1px solid ${theme.palette.grey20}`,
      padding: '16px',
      verticalAlign: 'top',
      WebkitTapHighlightColor: 'transparent',
      '&:first-child': {
        overflow: 'hidden',
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6,
        border: 'none',
      },
      '&:last-child': {
        overflow: 'hidden',
        borderBottomLeftRadius: 6,
        borderBottomRightRadius: 6,
      },
      '& .MuiIconButton-root:last-child': {
        alignSelf: 'flex-start',
        marginTop: theme.spacing(-0.25),
        marginBottom: theme.spacing(-0.25),
        transition: theme.transitions.create('color'),
        '&:hover, &:focus': {
          color: theme.palette.primary.main,
        },
      },
    },
    listItemClickable: {
      '&:hover, &:focus': {
        color: theme.palette.primary.main,
        outline: 'none',
      },
    },
    listItemLink: {
      color: theme.palette.text.primary,
      overflowWrap: 'break-word',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      pointerEvents: 'all',
      cursor: 'pointer',
      '& svg': {
        transition: theme.transitions.create('color'),
      },
      '&:hover, &:focus': {
        color: theme.palette.primary.main,
        outline: 'none',
        '& svg': {
          color: theme.palette.primary.main,
        },
      },
    },
  });
});

export type IndexListItems = Array<{
  path?: string | null;
  id: string | number;
  style?: React.CSSProperties;
  onClick?: (e: React.SyntheticEvent) => void;
  children: React.ReactNode;
}>;

export type IndexListProps = BoxProps & { items: IndexListItems };

export default function IndexList({ items, ...props }: IndexListProps) {
  const classes = useStyles();
  const router = useRouter();
  return (
    <Box
      component="ul"
      role="list"
      margin={0}
      paddingLeft={0}
      {...props}
      className={classes.list}
    >
      {items.map((row) => {
        const onClick = (event: React.MouseEvent<HTMLLIElement>) => {
          if (typeof row.onClick === 'function') {
            row.onClick(event);
          } else if (row.path) {
            navigateToPathOnClick(event, router, row.path);
          }
        };
        const Icon = row.path?.startsWith('http') ? PopOutIcon : ArrowRightIcon;
        const linkIcon = (row.path || row.onClick) && (
          <Icon
            height={20}
            width={28}
            color="secondary.main"
            paddingLeft={1}
            flexShrink={0}
          />
        );
        return (
          <Box
            key={row.id}
            component="li"
            className={clsx(
              classes.listItem,
              (row.path || row.onClick) && classes.listItemClickable
            )}
            tabIndex={typeof row.onClick === 'function' ? 0 : undefined}
            onAuxClick={onClick}
            onClick={onClick}
            style={row?.style}
          >
            {row.path ? (
              row.path.startsWith('http') ? (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={row.path}
                  className={classes.listItemLink}
                >
                  {row.children}
                  {linkIcon}
                </a>
              ) : (
                <BoxLink href={row.path} className={classes.listItemLink}>
                  {row.children}
                  {linkIcon}
                </BoxLink>
              )
            ) : (
              <>
                {row.children}
                {linkIcon}
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
